import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { submitEnquiry } from "@/lib/api";
import { toast } from "sonner";
import { PRODUCTS } from "@/data/products";
import { Zap, CheckCircle2 } from "lucide-react";

export function EnquiryDialog({ open, onOpenChange, defaultProduct, source }) {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    product: defaultProduct || "General Enquiry",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (defaultProduct) setForm((f) => ({ ...f, product: defaultProduct }));
  }, [defaultProduct]);

  useEffect(() => {
    if (!open) {
      setTimeout(() => setSubmitted(false), 300);
    }
  }, [open]);

  const update = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: typeof e === "string" ? e : e.target.value,
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    try {
      setLoading(true);
      await submitEnquiry({ ...form, source: source || "enquiry-dialog" });
      setSubmitted(true);
      toast.success("Thanks! Our team will reach out within 24 hours.");
      setForm({
        name: "",
        company: "",
        email: "",
        phone: "",
        product: defaultProduct || "General Enquiry",
        subject: "",
        message: "",
      });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl p-0 overflow-hidden bg-white"
        data-testid="enquiry-dialog"
      >
        <div className="grid md:grid-cols-5">
          {/* Left visual */}
          <div className="hidden md:block md:col-span-2 bg-vortex-navy text-white p-8 relative overflow-hidden">
            <div className="absolute inset-0 grid-pattern opacity-30" />
            <div className="relative z-10 h-full flex flex-col justify-between">
              <div>
                <div className="w-11 h-11 rounded-lg bg-vortex-blue flex items-center justify-center mb-6">
                  <Zap
                    className="w-5 h-5 text-vortex-navy"
                    strokeWidth={2.5}
                    fill="currentColor"
                  />
                </div>
                <h3 className="font-heading font-bold text-2xl leading-tight mb-3">
                  Talk to our power engineers.
                </h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  Custom sizing, load analysis and same-day quotations for
                  industrial power protection.
                </p>
              </div>
              <ul className="space-y-3 text-sm text-white/85">
                {[
                  "Response within 24 hours",
                  "Free load analysis",
                  "Pan-India delivery",
                  "5-year warranty",
                ].map((f) => (
                  <li key={f} className="flex items-center gap-2.5">
                    <CheckCircle2
                      className="w-4 h-4 text-vortex-blue"
                      strokeWidth={2}
                    />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right form */}
          <div className="md:col-span-3 p-6 md:p-8">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-vortex-navy">
                Request a Quote
              </DialogTitle>
              <DialogDescription className="text-vortex-slate">
                Fill in your details — we&apos;ll get back within 24 hours.
              </DialogDescription>
            </DialogHeader>

            {submitted ? (
              <div className="py-10 text-center" data-testid="enquiry-success">
                <div className="w-14 h-14 rounded-full bg-vortex-blue/10 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2
                    className="w-7 h-7 text-vortex-blue"
                    strokeWidth={2}
                  />
                </div>
                <h4 className="font-heading font-bold text-xl text-vortex-navy mb-2">
                  Enquiry received!
                </h4>
                <p className="text-vortex-slate text-sm max-w-sm mx-auto">
                  Our team has been notified and will call you shortly. You can
                  close this dialog.
                </p>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4"
              >
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-vortex-navy uppercase tracking-widest">
                    Name *
                  </label>
                  <Input
                    required
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your full name"
                    className="mt-1.5 rounded-xl bg-vortex-alt border-vortex-border focus:border-vortex-blue focus:ring-vortex-blue/20"
                    data-testid="enquiry-name-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-vortex-navy uppercase tracking-widest">
                    Phone *
                  </label>
                  <Input
                    required
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="+91 98765 43210"
                    className="mt-1.5 rounded-xl bg-vortex-alt border-vortex-border focus:border-vortex-blue focus:ring-vortex-blue/20"
                    data-testid="enquiry-phone-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-vortex-navy uppercase tracking-widest">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@company.com"
                    className="mt-1.5 rounded-xl bg-vortex-alt border-vortex-border focus:border-vortex-blue focus:ring-vortex-blue/20"
                    data-testid="enquiry-email-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-vortex-navy uppercase tracking-widest">
                    Company
                  </label>
                  <Input
                    value={form.company}
                    onChange={update("company")}
                    placeholder="Your organisation"
                    className="mt-1.5 rounded-xl bg-vortex-alt border-vortex-border focus:border-vortex-blue focus:ring-vortex-blue/20"
                    data-testid="enquiry-company-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-vortex-navy uppercase tracking-widest">
                    Product
                  </label>
                  <Select
                    value={form.product}
                    onValueChange={(v) => setForm((f) => ({ ...f, product: v }))}
                  >
                    <SelectTrigger
                      className="mt-1.5 rounded-xl bg-vortex-alt border-vortex-border h-10"
                      data-testid="enquiry-product-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="General Enquiry">
                        General Enquiry
                      </SelectItem>
                      {PRODUCTS.map((p) => (
                        <SelectItem key={p.slug} value={p.name}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-vortex-navy uppercase tracking-widest">
                    Message
                  </label>
                  <Textarea
                    rows={3}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Load rating, application, quantity, timeline…"
                    className="mt-1.5 rounded-xl bg-vortex-alt border-vortex-border focus:border-vortex-blue focus:ring-vortex-blue/20"
                    data-testid="enquiry-message-input"
                  />
                </div>
                <div className="sm:col-span-2 flex items-center justify-between mt-2">
                  <p className="text-[11px] text-vortex-slate">
                    Your details are safe with us. No spam, ever.
                  </p>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="rounded-full bg-vortex-orange hover:bg-vortex-orange-deep text-white px-7 h-11 font-semibold shadow-lg hover:shadow-orange-500/25 transition-all"
                    data-testid="enquiry-submit-btn"
                  >
                    {loading ? "Sending…" : "Send Enquiry"}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
